/* import __COLOCATED_TEMPLATE__ from './typeahead-question.hbs'; */
import { action } from '@ember/object';
import { service } from '@ember/service';
import { task } from 'ember-concurrency';
import { testAwareTimeout, waitForNext } from 'ember-simplepractice/utils/waiters';
import { tracked } from '@glimmer/tracking';
import Component from '@glimmer/component';
import ENV from 'client-portal/config/environment';

const DEFAULT_RESULT_LIMIT = ENV.maxPageSize;
const DEFAULT_OPTION_PROPERTY = 'name';
const DEFAULT_SEARCH_PARAM = 'name';

export default class RequestPrescreenerQuestionTypesTypeaheadQuestion extends Component {
  @service store;

  @tracked preloadedRecords = [];

  constructor() {
    super(...arguments);
    if (this.modelName) {
      this.preloadRecordsTask.perform();
    }
  }

  get isPreloaded() {
    return this.args.question?.typeaheadIsPreloaded;
  }

  get modelName() {
    return this.args.question?.typeaheadModelName;
  }

  get optionName() {
    return this.args.question?.typeaheadOptionProperty ?? DEFAULT_OPTION_PROPERTY;
  }

  get fieldList() {
    return this.args.question?.typeaheadFields;
  }

  get searchParam() {
    return this.args.question?.typeaheadSearchParam ?? DEFAULT_SEARCH_PARAM;
  }

  get selectedRecord() {
    let recordId = this.args.validationModel?.value;
    if (!recordId || !this.modelName) return null;

    return this.store.peekRecord(this.modelName, recordId);
  }

  get placeholder() {
    return this.args.question?.hint ?? null;
  }

  buildQueryParams(searchTerm = '') {
    if (!this.modelName) return {};

    let params = {
      page: {
        size: DEFAULT_RESULT_LIMIT,
      },
    };

    if (this.fieldList) {
      params.fields = {
        [this.modelName]: this.fieldList,
      };
    }

    if (searchTerm) {
      params.filter = {
        [this.searchParam]: searchTerm,
      };
    }

    return params;
  }

  async preselectSingleRecord(existingRecords) {
    if (existingRecords.length === 1) {
      await waitForNext();
      this.handleSelection(existingRecords.firstObject);
    }
  }

  @(task(function* () {
    if (!this.modelName) {
      this.preloadedRecords = [];
      return this.preloadedRecords;
    }

    let existingRecords = this.store.peekAll(this.modelName);
    this.preselectSingleRecord(existingRecords);
    this.preloadedRecords = existingRecords;

    if (existingRecords.length >= DEFAULT_RESULT_LIMIT) {
      this.preloadedRecords = existingRecords.slice(0, DEFAULT_RESULT_LIMIT);
      return this.preloadedRecords;
    }

    if (!this.isPreloaded) {
      let result = yield this.store.query(this.modelName, this.buildQueryParams());
      this.preloadedRecords = result.toArray();
      return this.preloadedRecords;
    }
  }).drop())
  preloadRecordsTask;

  @(task(function* (searchTerm = '') {
    if (!this.modelName) {
      return [];
    }

    if (!searchTerm) {
      return this.preloadedRecords;
    }

    if (searchTerm) {
      yield testAwareTimeout(500);
    }

    let query = this.buildQueryParams(searchTerm);
    let records = yield this.store.query(this.modelName, query);

    this.preloadedRecords = records.toArray();

    return records;
  }).restartable())
  searchRecordsTask;

  @action
  handleSelection(record) {
    let value = record?.id ?? null;
    this.args.onChange?.(this.args.question, value);
  }
}
